import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { NgbAlertModule } from '@ng-bootstrap/ng-bootstrap';

import { AuthService } from '../../services/auth.service';
import { AuthUser } from '../../interfaces/auth.interface';
import { StaffHeaderComponent } from '../staff-header/staff-header.component';
import { StaffNavComponent } from '../staff-nav/staff-nav.component';
import { environment } from '../../../environments/environment';

// CPD Course Detail Interface (API Response)
export interface CpdCourseDetail {
  id: string;
  hoursStudied: string;
  studyDate: string;
  year: number;
  sessionTitle: string;
  notes: string;
  isVerified: boolean;
  enrolledAt: string;
  completedAt: string | null;
  progress: string;
  isCompleted: boolean;
  createdAt: string;
  updatedAt: string;
  userId: string;
  courseId: string;
  cpdCourseStatusId: number;
  cpdPaymentStatusId: number;
  user: {
    id: string;
    email: string;
    password: string;
    cid: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    companyId: string | null;
  };
  course: {
    id: string;
    title: string;
    description: string;
    instructor: string;
    duration: string;
    category: string;
    level: string;
    thumbnail: string | null;
    price: string;
    seat: number;
    regisSeat: number;
    isActive: boolean;
    startDate: string;
    endDate: string;
    createdAt: string;
    updatedAt: string;
    courseStatusId: number;
  };
  cpdCourseStatus?: {
    id: number;
    name: string;
  };
  cpdPaymentStatus: {
    id: number;
    name: string;
    createdAt: string;
    updatedAt: string;
  };
  cpdConfirmStatus?: {
    id: number;
    name: string;
  };
  imageThumbnail?: string | null;
  paymentSlip?: string | null;
  transferDateTime?: string | null;
}

@Component({
  selector: 'app-cpd-course-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgbAlertModule,
    StaffHeaderComponent,
    StaffNavComponent
  ],
  templateUrl: './cpd-course-detail.component.html',
  styleUrl: './cpd-course-detail.component.scss'
})
export class CpdCourseDetailComponent implements OnInit, OnDestroy {
  currentUser: AuthUser | null = null;
  isMobileMenuOpen = false;
  private userSubscription: Subscription | undefined;

  cpdHourId: string | null = null;
  cpdDetail: CpdCourseDetail | null = null;
  isLoading = false; // Start with false to show content immediately
  error: string | null = null;
  successMessage: string | null = null;
  showImagePreview = false;
  previewImageUrl: string | null = null;

  // Status options
  confirmationStatusOptions = [
    { id: 1, name: 'รอ Approve' },
    { id: 2, name: 'ยกเลิกใบคำขอ' },
    { id: 3, name: 'Approved' },
    { id: 4, name: 'คำขอเก่า' }
  ];

  verificationOptions = [
    { value: false, label: 'รอยืนยัน' },
    { value: true, label: 'ยืนยันแล้ว' }
  ];

  completionOptions = [
    { value: false, label: 'ยังไม่เสร็จสมบูรณ์' },
    { value: true, label: 'เสร็จสมบูรณ์' }
  ];

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log('🔄 CpdCourseDetailComponent ngOnInit');

    // Subscribe to auth user
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      console.log('👤 Current user updated:', user);
      this.currentUser = user;
      this.cdr.detectChanges();
    });

    // Get CPD Hour ID from route params
    this.route.paramMap.subscribe(params => {
      this.cpdHourId = params.get('id');
      console.log('📋 CPD Hour ID from route:', this.cpdHourId);

      if (this.cpdHourId) {
        this.loadCpdDetail();
      } else {
        this.error = 'ไม่พบรหัส CPD Hour';
      }
    });
  }

  ngOnDestroy(): void {
    console.log('🗑️ CpdCourseDetailComponent ngOnDestroy');
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  onLogout(): void {
    this.authService.logout();
  }

  closeMobileMenu(): void {
    this.isMobileMenuOpen = false;
  }

  onNavigate(route: string): void {
    this.closeMobileMenu();

    switch (route) {
      case 'users':
        this.router.navigate(['/staff/user-management']);
        break;
      case 'courses':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'reports':
        this.router.navigate(['/staff/dashboard']);
        break;
      case 'settings':
        this.router.navigate(['/staff/dashboard']);
        break;
      default:
        this.router.navigate(['/staff/dashboard']);
        break;
    }
  }

  /**
   * Load CPD course detail from API
   */
  loadCpdDetail(): void {
    if (!this.cpdHourId) return;

    console.log('🚀 Loading CPD course detail from API...');
    // Don't set isLoading to true - show content immediately
    this.error = null;

    const apiUrl = `${environment.apiUrl}/cpd-hours/${this.cpdHourId}`;
    console.log('📡 Fetching from:', apiUrl);

    this.http.get<CpdCourseDetail>(apiUrl)
      .subscribe({
        next: (response) => {
          console.log('✅ API Response:', response);
          console.log('✅ Response status: Success (200 or 304)');
          this.cpdDetail = response;

          // Manually trigger change detection to update the view
          // This is critical for HTTP 304 cached responses
          this.cdr.detectChanges();
          console.log('🔄 Change detection triggered - content should display now');
        },
        error: (error: HttpErrorResponse) => {
          console.error('❌ API Error:', error);
          console.error('❌ Error status:', error.status);
          console.error('❌ Error message:', error.message);

          this.error = `เกิดข้อผิดพลาดในการโหลดข้อมูล: ${error.message}`;
          this.cdr.detectChanges();
        }
      });
  }

  /**
   * Go back to CPD Hours Management
   */
  goBack(): void {
    this.router.navigate(['/staff/cpd-hours-management']);
  }

  /**
   * Format date for display (Thai format)
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('th-TH', {
      // Course dates are naive wall-clock tagged UTC; format in UTC to avoid
      // the +7h (ICT) shift that rolls evening times to the next calendar day.
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Format date and time for display (Thai format)
   */
  formatDateTime(dateString: string | null): string {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('th-TH', {
      // Wall-clock time was stored tagged UTC; display in UTC so 17:00 stays
      // 17:00 instead of shifting +7h to 00:00 of the next day.
      timeZone: 'UTC',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Format price for display
   */
  formatPrice(price: string): string {
    const numPrice = parseFloat(price);
    return numPrice.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /**
   * Get level text in Thai
   */
  getLevelText(level: string): string {
    switch (level) {
      case 'beginner':
        return 'เริ่มต้น';
      case 'intermediate':
        return 'กลาง';
      case 'advanced':
        return 'สูง';
      default:
        return level;
    }
  }

  /**
   * Get level badge class
   */
  getLevelBadgeClass(level: string): string {
    switch (level) {
      case 'beginner':
        return 'bg-info';
      case 'intermediate':
        return 'bg-primary';
      case 'advanced':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get CPD Course Status badge class
   */
  getCpdCourseStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ยืนยันแล้ว':
      case 'อนุมัติ':
        return 'bg-success';
      case 'รอยืนยัน':
      case 'รอการอนุมัติ':
      case 'รอ Approve':
        return 'bg-warning';
      case 'ไม่อนุมัติ':
      case 'ยกเลิก':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get Confirmation Status badge class
   */
  getConfirmationStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'Approved':
        return 'bg-success';
      case 'รอ Approve':
        return 'bg-warning';
      case 'ยกเลิกใบคำขอ':
        return 'bg-danger';
      case 'คำขอเก่า':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get Confirmation Status name by ID
   */
  getConfirmationStatusName(statusId: number): string {
    const status = this.confirmationStatusOptions.find(s => s.id === statusId);
    return status ? status.name : 'ไม่ระบุ';
  }

  /**
   * Get CPD Payment Status badge class
   */
  getCpdPaymentStatusBadgeClass(statusName: string): string {
    switch (statusName) {
      case 'ชำระเงินเสร็จสิ้น':
      case 'ชำระเงินแล้ว':
      case 'ชำระแล้ว':
        return 'bg-success';
      case 'รอชำระเงิน':
      case 'ยังไม่ได้ชำระเงิน':
        return 'bg-warning';
      case 'รอตรวจสอบยอดชำระ':
      case 'รอตรวจสอบ':
        return 'bg-info';
      case 'ยกเลิกการชำระ':
      case 'ยกเลิก':
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get completion badge class
   */
  getCompletionBadgeClass(isCompleted: boolean): string {
    return isCompleted ? 'bg-success' : 'bg-secondary';
  }

  /**
   * Get completion text
   */
  getCompletionText(isCompleted: boolean): string {
    return isCompleted ? 'เสร็จสมบูรณ์' : 'ยังไม่เสร็จสมบูรณ์';
  }

  /**
   * Get active status badge class
   */
  getActiveStatusBadgeClass(isActive: boolean): string {
    return isActive ? 'bg-success' : 'bg-secondary';
  }

  /**
   * Get active status text
   */
  getActiveStatusText(isActive: boolean): string {
    return isActive ? 'เปิดใช้งาน' : 'ปิดใช้งาน';
  }

  /**
   * Update confirmation status
   */
  updateConfirmationStatus(newStatusId: number): void {
    if (!this.cpdDetail || !this.cpdHourId) return;

    console.log('✅ Updating confirmation status to:', newStatusId);

    const apiUrl = `${environment.apiUrl}/cpd-hours/${this.cpdHourId}`;

    // Send cpdCourseStatusId in the request body as required by the API
    this.http.patch(apiUrl, { cpdCourseStatusId: newStatusId }).subscribe({
      next: (response: any) => {
        console.log('✅ Confirmation status updated:', response);
        this.successMessage = 'อัปเดตสถานะการยืนยันสำเร็จ';
        this.loadCpdDetail(); // Reload data
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating confirmation status:', error);
        this.error = `เกิดข้อผิดพลาดในการอัปเดตสถานะการยืนยัน: ${error.message}`;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Update verification status (legacy method - kept for backward compatibility)
   */
  updateVerificationStatus(isVerified: boolean): void {
    if (!this.cpdDetail || !this.cpdHourId) return;

    console.log('✅ Updating verification status to:', isVerified);

    const apiUrl = `${environment.apiUrl}/cpd-hours/${this.cpdHourId}`;

    this.http.patch(apiUrl, { isVerified }).subscribe({
      next: (response: any) => {
        console.log('✅ Verification status updated:', response);
        this.successMessage = 'อัปเดตสถานะการยืนยันสำเร็จ';
        this.loadCpdDetail(); // Reload data
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating verification status:', error);
        this.error = `เกิดข้อผิดพลาดในการอัปเดตสถานะการยืนยัน: ${error.message}`;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Update completion status
   */
  updateCompletionStatus(isCompleted: boolean): void {
    if (!this.cpdDetail || !this.cpdHourId) return;

    console.log('🎓 Updating completion status to:', isCompleted);

    const apiUrl = `${environment.apiUrl}/cpd-hours/${this.cpdHourId}`;

    this.http.patch(apiUrl, { isCompleted }).subscribe({
      next: (response: any) => {
        console.log('✅ Completion status updated:', response);
        this.successMessage = 'อัปเดตสถานะการเรียนจบสำเร็จ';
        this.loadCpdDetail(); // Reload data
        setTimeout(() => this.successMessage = null, 3000);
      },
      error: (error: HttpErrorResponse) => {
        console.error('❌ Error updating completion status:', error);
        this.error = `เกิดข้อผิดพลาดในการอัปเดตสถานะการเรียนจบ: ${error.message}`;
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Open image preview modal
   */
  openImagePreview(imageUrl: string): void {
    this.previewImageUrl = imageUrl;
    this.showImagePreview = true;
  }

  /**
   * Close image preview modal
   */
  closeImagePreview(): void {
    this.showImagePreview = false;
    this.previewImageUrl = null;
  }
}
